import { CustomerViewModel, TenantViewModel, UserViewModel } from '@ail/customers-api'
import React, { Component } from 'react'
import { WithTranslation, withTranslation } from 'react-i18next'
import { ConnectedProps, connect } from 'react-redux'
import { NavLink, RouteComponentProps } from 'react-router-dom'
import { FrontendSettings } from '../../../helpers/FrontendSettings'
import IdIsCopied from '../../../helpers/IdIsCopied'
import { wrapQuery } from '../../../redux/api/apiQuery'
import { customersApi } from '../../../redux/api/customerApi'
import { State } from '../../../redux/reducers/initialState'
import { newItemId } from '../../../types/constants'
import { FrontendSettingsKeys, PermissionsEnum } from '../../../types/enums'
import { ITenantViewModel } from '../../../types/interfaces'
import Confirm from '../../common/Confirm/Confirm'
import './UsersAdminPanel.scss'
import ShowUsersUnits from './showUsersUnits/ShowUsersUnitsFunctional'
import { Loader } from '../../common/Loader/Loader'

type UsersAdminPanelProps = ConnectedProps<typeof connector> & WithTranslation & RouteComponentProps

interface CompanyForFilter {
  id: string
  name: string
}

interface UpdatedUserWithCompanies extends UserViewModel {
  allCompaniesList: string[]
}

interface UsersAdminPanelState {
  customers: CustomerViewModel[]
  statusInFilter?: boolean
  usersUpdated: boolean
  actualCustomers: CompanyForFilter[]
  customersFromUnits: CompanyForFilter[]
  userEnabled: boolean
  showUsersUnits: boolean
  showGroupPopup: boolean
  showCompanyPopup: boolean
  selectedTenantId: string
  selectedCompanyId: string
  selectedUserId: string
  selectedUserName: string
  allUsers: UpdatedUserWithCompanies[]
  userId: string
  loading: boolean
  customerSearchString: string
  groupSearchString: string
  userSearchString?: string
  matrixView: boolean
  showEditConfirm: boolean
  idCopied: boolean
  tenants: TenantViewModel[]
  users: UserViewModel[]
}

const permissions = Object.freeze({
  reportsView: PermissionsEnum.ReportsView.toString(),
  ordersApprove: PermissionsEnum.RequestsApprove.toString(),
  ordersManage: PermissionsEnum.OrdersManage.toString(),
  documentsView: PermissionsEnum.DocumentsView.toString(),
  costsView: PermissionsEnum.CostsView.toString(),
})

class UsersAdminPanel extends Component<UsersAdminPanelProps, UsersAdminPanelState> {
  constructor(props: UsersAdminPanelProps) {
    super(props)
    this.state = {
      customers: [],
      usersUpdated: false,
      matrixView: false,
      actualCustomers: [],
      customersFromUnits: [],
      userEnabled: false,
      showEditConfirm: false,
      showUsersUnits: false,
      showGroupPopup: false,
      showCompanyPopup: false,
      selectedCompanyId: FrontendSettings.get(FrontendSettingsKeys.companyIdInUsersFilter) || '',
      selectedUserName: '',
      userSearchString: FrontendSettings.get(FrontendSettingsKeys.userKeywordFilter) || '',
      selectedUserId: '',
      groupSearchString: '',
      customerSearchString: '',
      userId: newItemId,
      loading: false,
      allUsers: [],
      selectedTenantId: FrontendSettings.get(FrontendSettingsKeys.preselectedTenantId) || '',
      idCopied: false,
      tenants: [],
      users: [],
    }
  }

  componentDidMount() {
    if (FrontendSettings.get(FrontendSettingsKeys.matrixViewMode)) {
      this.setState({
        matrixView: true,
      })
      FrontendSettings.clear(FrontendSettingsKeys.matrixViewMode)
    }
    FrontendSettings.clear(FrontendSettingsKeys.companyIdInUsersFilter)
    this.setState({
      loading: true,
    })
    this.props.loadTenants().then((tenants) => {
      this.setState({ tenants })
      const { selectedTenantId } = this.state
      if (selectedTenantId) {
        this.props.loadCustomersAdmin(selectedTenantId).then((customers) => {
          this.setState({ customers })
          this.props.loadUsers(selectedTenantId).then((users) => this.setState({ users, loading: false }))
        })
      } else {
        this.setState({ loading: false })
      }
    })
  }

  componentDidUpdate(oldProps: UsersAdminPanelProps, oldState: UsersAdminPanelState) {
    if (this.state.users !== oldState.users && this.state.users.length > 0) {
      this.updateAllUsers()
    }

    if (this.state.idCopied !== oldState.idCopied) {
      setTimeout(() => {
        this.setState({
          idCopied: false,
        })
      }, 1000)
    }

    if (
      this.state.selectedCompanyId !== oldState.selectedCompanyId ||
      this.state.selectedTenantId !== oldState.selectedTenantId
    ) {
      this.onUserSearchChange(undefined)
    }

    if (
      this.state.selectedTenantId !== oldState.selectedTenantId ||
      this.state.selectedCompanyId !== oldState.selectedCompanyId ||
      (this.state.usersUpdated !== oldState.usersUpdated && this.state.usersUpdated && this.state.selectedCompanyId)
    ) {
      if (this.state.selectedCompanyId) {
        const users = [...this.state.allUsers]
        const updatedUsers: UpdatedUserWithCompanies[] = []
        users.map((c) => {
          c.customers.map((el) => {
            if (el.id.toLowerCase().indexOf(this.state.selectedCompanyId.toLowerCase()) > -1) {
              updatedUsers.push(c)
            }
          })
          if (c.units.length > 0) {
            c.units.map((el) => {
              if (el.customerId.toLowerCase().indexOf(this.state.selectedCompanyId.toLowerCase()) > -1) {
                if (!updatedUsers.includes(c)) {
                  updatedUsers.push(c)
                }
              }
            })
          }
        })

        this.setState({
          allUsers: updatedUsers,
        })
      } else {
        this.updateAllUsers()
      }
    }
  }

  public render() {
    const { t } = this.props
    const {
      tenants,
      loading,
      userEnabled,
      selectedTenantId,
      userSearchString,
      showEditConfirm,
      selectedCompanyId,
      customers,
    } = this.state
    let groupName = ''
    let companyName = ''
    if (tenants) {
      tenants.map((obj) => {
        if (obj.id === selectedTenantId) {
          groupName = obj.name
        }
      })
    }
    if (customers) {
      customers.map((el) => {
        if (el.id === selectedCompanyId) {
          companyName = el.name
        }
      })
    }
    return (
      <>
        <Loader blocking={loading} style={{ overflow: 'hidden' }}>
          <ul className="breadcrumbs">
            <li>
              <NavLink to="/UserManagement">
                <span>{t('ADMIN_PANEL')}</span>
              </NavLink>
            </li>
            {!!groupName && (
              <li>
                <NavLink to="/UserManagement/groups">
                  <span>{groupName}</span>
                </NavLink>
              </li>
            )}
            {!!companyName && (
              <li>
                <span>{companyName}</span>
              </li>
            )}
            <li>
              <span>{t('USERS')}</span>
            </li>
            {this.props.currentUser?.isInternalUser && (
              <li>
                <span>
                  <a onClick={() => this.switchMatrixView()} className="matrixLink">
                    Users' permissions view <i className="icon-matrix"></i>
                  </a>
                </span>
              </li>
            )}
          </ul>
          <div className="row adminArea companiesArea">
            <div className="col-6 col-md-3">
              <div className="box selectArea">
                <div className="select">
                  <input
                    name={`fieldnameveryunique${new Date().getTime()}`}
                    autoComplete="none"
                    list="autocompleteOff"
                    placeholder={t('SELECT_TENANT')}
                    onClick={() => this.toggleGroupORCompanyPopup(true)}
                    value={groupName}
                    readOnly
                  />

                  <i className="icon-bottom"></i>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="box selectArea">
                <div className="select">
                  <input
                    name={`fieldnameveryunique${new Date().getTime()}`}
                    autoComplete="none"
                    list="autocompleteOff"
                    placeholder={t('SELECT_COMPANY')}
                    onClick={() => this.toggleGroupORCompanyPopup(false)}
                    value={companyName}
                    readOnly
                  />
                  {!!companyName && <i onClick={() => this.clearCustomer()} className="icon-clear"></i>}
                  <i className="icon-bottom"></i>
                </div>
              </div>
            </div>
            <div style={{ margin: '0', paddingBottom: '0', maxWidth: 'none' }} className="col-6 col-md-3 searchArea">
              <div style={{ maxWidth: 'none' }} className="box boxArea searchBar">
                <input
                  type="search"
                  name={`fieldnameveryunique${new Date().getTime()}`}
                  autoComplete="none"
                  list="autocompleteOff"
                  value={userSearchString}
                  placeholder={t('FIND_USERS_BY_KEYWORD')}
                  onChange={(e) => this.onUserSearchChange(e)}
                />
                {!!userSearchString && <i className="icon-clear" onClick={() => this.onUserSearchChange(undefined)} />}
                <i className="icon-search"></i>
              </div>
            </div>
            {!!selectedTenantId &&
              (this.props.currentUser?.isAdmin ||
                (this.props.currentUser?.isInternalUser && this.props.currentUser?.isManager)) && (
                <div className="col-6 col-md-3">
                  <div className="box groupArea newBox" onClick={() => this.moveToUserCreate()}>
                    <i className="icon-add" /> {t('CREATE_NEW_USER')}
                  </div>
                </div>
              )}
            {!this.state.matrixView ? (
              <div className="itemList">
                {this.state.allUsers.length > 0 &&
                  this.state.allUsers
                    .sort((a, b) => a.lastName.localeCompare(b.lastName))
                    .map(
                      (obj, i) =>
                        (!userSearchString ||
                          obj.displayName.toLowerCase().indexOf(userSearchString.toLowerCase()) > -1 ||
                          obj.name.toLowerCase().indexOf(userSearchString.toLowerCase()) > -1) && (
                          <div key={i} className="col-6 col-md-3">
                            <div className="box groupArea userArea">
                              <div className="itemTitle">
                                <span>
                                  <i className="icon-user" />
                                  {obj.displayName}
                                </span>
                                <div style={{ display: 'flex', gap: '10px' }}>
                                  <label className="switch">
                                    <input
                                      type="checkbox"
                                      onChange={() => this.handleUserToEdit(obj.id, obj.enabled, obj.name)}
                                      checked={obj.enabled ? true : false}
                                    />
                                    <span className="slider round" />
                                  </label>
                                </div>
                              </div>
                              <div className="userInfoArea">
                                <div className="infoComp">
                                  <label>{t('USER_NAME')}</label>
                                  {obj.name}
                                </div>
                                <div className="infoComp">
                                  <label>{t('ROLE')}</label>
                                  <div className="flex">
                                    {obj.isManager
                                      ? t('MANAGER')
                                      : obj.isGuest
                                      ? t('GUEST')
                                      : obj.isSupplier
                                      ? t('SUPPLIER')
                                      : obj.isCustomer
                                      ? t('CUSTOMER')
                                      : t('REGULAR_USER')}
                                    <div className="idCopy">
                                      <i
                                        style={
                                          this.props.currentUser?.isAdmin ||
                                          (this.props.currentUser?.isInternalUser && this.props.currentUser?.isManager)
                                            ? { cursor: 'pointer' }
                                            : undefined
                                        }
                                        onClick={() => this.copyUserId(obj.id)}
                                        className="icon-id"
                                      ></i>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="flex">
                                <div className="itemAction">
                                  <NavLink to="/UserManagement/companies">
                                    <div className="action">
                                      <div className="table-cell">
                                        <i className="icon-customers" />
                                        {t('COMPANIES')}
                                      </div>
                                    </div>
                                  </NavLink>
                                </div>
                                <div onClick={() => this.toggleUsersUnits(obj.id)} className="itemAction">
                                  <div className="action">
                                    <div className="table-cell">
                                      <i className="icon-units" />
                                      {t('UNITS')}
                                    </div>
                                  </div>
                                </div>
                                <div className="itemAction">
                                  <NavLink
                                    to={`/UserManagement/users/newUserEdit/${this.state.selectedTenantId}/${obj.id}/duplicate`}
                                  >
                                    <div className="action">
                                      <div className="table-cell">
                                        <i className="icon-duplicate" />
                                        {t('DUPLICATE')}
                                      </div>
                                    </div>
                                  </NavLink>
                                </div>
                                <div className="itemAction">
                                  <NavLink
                                    to={`/UserManagement/users/newUserEdit/${this.state.selectedTenantId}/${obj.id}`}
                                  >
                                    <div className="action">
                                      <div className="table-cell">
                                        <i
                                          className={
                                            this.props.currentUser?.isAdmin ||
                                            (this.props.currentUser?.isInternalUser &&
                                              this.props.currentUser?.isManager)
                                              ? 'icon-edit'
                                              : 'icon-show'
                                          }
                                        />
                                        {this.props.currentUser?.isAdmin ||
                                        (this.props.currentUser?.isInternalUser && this.props.currentUser?.isManager)
                                          ? t('EDIT')
                                          : t('VIEW')}
                                      </div>
                                    </div>
                                  </NavLink>
                                </div>
                              </div>
                            </div>
                          </div>
                        )
                    )}
              </div>
            ) : (
              <div className="col-12 reqArea reqManArea">
                <div className="scrollableArea">
                  <div className="grid-wrap flat-no-paging usersMatrixView">
                    <ul className="reqHeader">
                      <li>
                        <div className="owner">
                          <i className="icon-user" /> {t('USER_NAME')}
                        </div>
                        <div className="owner">
                          <i className="icon-mail" /> {t('EMAIL')}
                        </div>
                        <div className="email">
                          <i className="icon-id" /> Miles ID
                        </div>
                        <div className="owner">
                          <i className="icon-groups" /> {t('GROUP')}
                        </div>
                        <div className="id">
                          <i className="icon-customers" /> {t('COMPANY')}
                        </div>
                        <div className="id">
                          <i className="icon-units" /> {t('UNITS')}
                        </div>
                        <div className="status selector">
                          <div className="selectArea">
                            <select onChange={(e) => this.statusFilterChange(e)} defaultValue={'ALL'}>
                              <option value={'ALL'}>{t('ALL')}</option>
                              <option value={'ACTIVE'}>{t('ACTIVE')}</option>
                              <option value={'DISABLED'}>{t('DISABLED')}</option>
                            </select>
                            <i className="icon-bottom" />
                          </div>
                          {t('STATUS')}
                        </div>
                        <div className="status">
                          <i className="icon-manager" /> {t('MANAGER')}
                        </div>
                        <div className="status">
                          <i className="icon-supplier" /> {t('SUPPLIER')}
                        </div>
                        <div className="status">
                          <i className="icon-report" /> {t('VIEW_REPORTS')}
                        </div>
                        <div className="status">
                          <i className="icon-requests" /> {t('APPROVE_REQUESTS')}
                        </div>
                        <div className="status">
                          <i className="icon-requests-man" /> {t('MANAGE_REQUESTS')}
                        </div>
                        <div className="status">
                          <i className="icon-doc" /> {t('VIEW_DOCUMENTS')}
                        </div>
                        <div className="status">
                          <i className="icon-cost" /> {t('VIEW_COSTS')}
                        </div>
                      </li>
                    </ul>
                    <ul className="reqList">
                      {this.state.allUsers.length > 0 &&
                        this.state.allUsers
                          .sort((a, b) => a.lastName.localeCompare(b.lastName))
                          .map(
                            (obj) =>
                              (!userSearchString ||
                                obj.displayName.toLowerCase().indexOf(userSearchString.toLowerCase()) > -1 ||
                                obj.name.toLowerCase().indexOf(userSearchString.toLowerCase()) > -1) &&
                              ((this.state.statusInFilter !== null && obj.enabled === this.state.statusInFilter) ||
                                this.state.statusInFilter == null) && (
                                <li key={obj.id}>
                                  <div className="owner">
                                    <a onClick={() => this.moveToUserRememberMode(obj.id)}>
                                      {obj.firstName + ' ' + obj.lastName}
                                    </a>
                                  </div>
                                  <div className="owner">
                                    <div>{obj.name}</div>
                                    <div className="tooltip">
                                      <div>{obj.name}</div>
                                    </div>
                                  </div>
                                  <div className="email">
                                    <div>{obj.externalReference || '-'}</div>
                                  </div>
                                  <div className="owner">
                                    <div>{groupName}</div>
                                    <div className="tooltip">
                                      <div>{groupName}</div>
                                    </div>
                                  </div>
                                  <div className="id">
                                    <div>
                                      {obj.allCompaniesList
                                        ?.sort((a, b) => a.localeCompare(b))
                                        .map((el) => el)
                                        .join('; ')}
                                    </div>
                                    {!!obj.allCompaniesList?.length && (
                                      <div className="tooltip">
                                        <div>
                                          {obj.allCompaniesList
                                            ?.sort((a, b) => a.localeCompare(b))
                                            .map((el) => el)
                                            .join('; ')}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <div className="id">
                                    <div>
                                      {obj.units.length > 0 &&
                                        obj.units.map((el) => el.name || t('NO_COST_CENTER')).join(', ') +
                                          (obj.customers.length > 0 ? ', ' : '')}
                                      {obj.customers.length > 0 && `${t('ALL_UNITS')}`}
                                    </div>
                                    {(obj.units.length > 0 || obj.customers.length > 0) && (
                                      <div className="tooltip">
                                        <div>
                                          {obj.units.length > 0 &&
                                            obj.units.map((el) => el.name || t('NO_COST_CENTER')).join(', ') +
                                              (obj.customers.length > 0 ? ', ' : '')}
                                          {obj.customers.length > 0 && `${t('ALL_UNITS')}`}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                  <div className="status selector">
                                    <label className="switch">
                                      <input
                                        type="checkbox"
                                        onChange={() => this.handleUserToEdit(obj.id, obj.enabled, obj.name)}
                                        checked={obj.enabled ? true : false}
                                        disabled
                                      />
                                      <span className="slider round" />
                                    </label>
                                  </div>
                                  <div className="status">
                                    <div>
                                      {obj.isManager ? <i className="icon-check" /> : <i className="icon-close" />}
                                    </div>
                                  </div>
                                  <div className="status">
                                    <div>
                                      {obj.isSupplier ? <i className="icon-check" /> : <i className="icon-close" />}
                                    </div>
                                  </div>
                                  <div className="status">
                                    <div>
                                      {obj.permissions.indexOf(permissions.reportsView) >= 0 ? (
                                        <i className="icon-check" />
                                      ) : (
                                        <i className="icon-close" />
                                      )}
                                    </div>
                                  </div>
                                  <div className="status">
                                    <div>
                                      {obj.permissions.indexOf(permissions.ordersApprove) >= 0 ? (
                                        <i className="icon-check" />
                                      ) : (
                                        <i className="icon-close" />
                                      )}
                                    </div>
                                  </div>
                                  <div className="status">
                                    <div>
                                      {obj.permissions.indexOf(permissions.ordersManage) >= 0 ? (
                                        <i className="icon-check" />
                                      ) : (
                                        <i className="icon-close" />
                                      )}
                                    </div>
                                  </div>
                                  <div className="status">
                                    <div>
                                      {obj.permissions.indexOf(permissions.documentsView) >= 0 ? (
                                        <i className="icon-check" />
                                      ) : (
                                        <i className="icon-close" />
                                      )}
                                    </div>
                                  </div>
                                  <div className="status">
                                    <div>
                                      {obj.permissions.indexOf(permissions.costsView) >= 0 ? (
                                        <i className="icon-check" />
                                      ) : (
                                        <i className="icon-close" />
                                      )}
                                    </div>
                                  </div>
                                </li>
                              )
                          )}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
          {((this.state.showGroupPopup && !this.state.loading) ||
            (this.state.showCompanyPopup && !this.state.loading)) && (
            <>
              <div className="popGroupSelect">
                <a
                  onClick={() => this.toggleGroupORCompanyPopup(this.state.showGroupPopup ? true : false)}
                  className="closeBtn"
                >
                  <i className="icon-close" />
                </a>
                <div className="itemTitle">
                  <label className="leftItemTitle">
                    {this.state.showGroupPopup ? t('SELECT_TENANT') : t('SELECT_COMPANY')}
                  </label>
                </div>
                <div className="centerInput">
                  <input
                    autoFocus={true}
                    name={`fieldnameveryunique${new Date().getTime()}`}
                    autoComplete="none"
                    list="autocompleteOff"
                    placeholder={this.state.showGroupPopup ? t('TENANT_NAME') : t('COMPANY_NAME')}
                    onChange={(e) => this.onSearchChange(e, this.state.showGroupPopup ? true : false)}
                  />
                  {(this.state.groupSearchString.length >= 2 || this.state.customerSearchString.length >= 2) && (
                    <ul>
                      {this.state.showGroupPopup
                        ? tenants.map(
                            (obj) =>
                              obj.name.toLowerCase().indexOf(this.state.groupSearchString.toLowerCase()) > -1 && (
                                <li onClick={() => this.onTenantChanged(obj)}>{obj.name}</li>
                              )
                          )
                        : this.state.customers.map(
                            (el, i) =>
                              el.name.toLowerCase().indexOf(this.state.customerSearchString.toLowerCase()) > -1 && (
                                <li key={i} onClick={() => this.onCompanyChanged(el.id)}>
                                  {el.name}
                                </li>
                              )
                            // }
                          )}
                    </ul>
                  )}
                </div>
              </div>
              <div className="blur" />
            </>
          )}
        </Loader>
        {this.state.showUsersUnits && (
          <ShowUsersUnits selectedUserId={this.state.selectedUserId} onClose={() => this.toggleUsersUnits()} />
        )}
        <IdIsCopied idCopied={this.state.idCopied} />
        <Confirm
          visible={showEditConfirm}
          onClose={(confirm: boolean) => {
            this.handleSubmit(confirm)
          }}
          title={t('PLEASE_CONFIRM')}
          message={`${userEnabled ? t('DO_YOU_WANT_TO_DISABLE') : t('DO_YOU_WANT_TO_ENABLE')} ${
            this.state.selectedUserName
          }?`}
        />
      </>
    )
  }

  private copyUserId(e: string) {
    navigator.clipboard.writeText(e)

    this.setState({
      idCopied: true,
    })
  }

  private statusFilterChange(e: React.ChangeEvent<HTMLSelectElement>) {
    const value = e.target.value
    if (value === 'ALL') {
      this.setState({
        statusInFilter: undefined,
      })
    }
    if (value === 'ACTIVE') {
      this.setState({
        statusInFilter: true,
      })
    }
    if (value === 'DISABLED') {
      this.setState({
        statusInFilter: false,
      })
    }
  }

  private moveToUserRememberMode(id: string) {
    FrontendSettings.set(FrontendSettingsKeys.matrixViewMode, 'y')
    this.props.history.push(`/UserManagement/users/newUserEdit/${this.state.selectedTenantId}/${id}`)
  }

  private updateAllUsers() {
    const users = [...this.state.users]
    const updatedUsers: UpdatedUserWithCompanies[] = []
    users.map((el) => {
      updatedUsers.push({ ...el, allCompaniesList: [] })
    })
    updatedUsers.map((el) => {
      el.customers.map((cust) => {
        el.allCompaniesList.push(cust.name)
      })
    })
    updatedUsers.map((el) => {
      this.state.customers.map((cust) => {
        el.units.map((un) => {
          if (cust.id === un.customerId && !el.allCompaniesList.includes(cust.name)) {
            el.allCompaniesList.push(cust.name)
          }
        })
      })
    })

    this.setState({
      allUsers: updatedUsers,
      usersUpdated: true,
    })
  }

  private switchMatrixView() {
    this.setState({
      matrixView: !this.state.matrixView,
      statusInFilter: undefined,
    })
  }

  private clearCustomer() {
    this.setState({
      selectedCompanyId: '',
    })
  }

  private toggleUsersUnits(userId?: string) {
    this.setState({
      showUsersUnits: !this.state.showUsersUnits,
      selectedUserId: userId || '',
    })
  }

  private toggleGroupORCompanyPopup(group: boolean) {
    if (group) {
      this.setState({
        showGroupPopup: !this.state.showGroupPopup,
        groupSearchString: '',
      })
    } else {
      this.setState({
        showCompanyPopup: !this.state.showCompanyPopup,
        customerSearchString: '',
      })
    }
  }

  private moveToUserCreate() {
    FrontendSettings.set(FrontendSettingsKeys.groupIdForUserCreate, this.state.selectedTenantId)
    const path = this.state.selectedTenantId
      ? `/UserManagement/users/newUserEdit/${this.state.selectedTenantId}/new`
      : '/UserManagement/users/newUserEdit/new'
    this.props.history.push(path)
  }

  private onUserSearchChange(e?: React.ChangeEvent<HTMLInputElement>) {
    if (e === null) {
      FrontendSettings.clear(FrontendSettingsKeys.userKeywordFilter)
      this.setState({
        userSearchString: '',
      })
    } else {
      const value = e?.target.value
      FrontendSettings.set(FrontendSettingsKeys.userKeywordFilter, value)
      this.setState({
        userSearchString: value,
      })
    }
  }

  private onSearchChange(e: React.ChangeEvent<HTMLInputElement>, group: boolean) {
    const { value } = e.target
    if (group) {
      this.setState({
        groupSearchString: value,
      })
    } else {
      this.setState({
        customerSearchString: value,
      })
    }
  }

  private handleSubmit(confirm: boolean) {
    if (confirm) {
      this.setState({
        loading: true,
      })
      const { selectedTenantId, userId } = this.state
      this.props
        .patchUserEnabled({ tenantId: this.state.selectedTenantId, userId, enabled: !this.state.userEnabled })
        .then(
          () => {
            this.props.loadUsers(selectedTenantId).then((users) => {
              this.setState({
                users,
                loading: false,
              })
            })
          },
          () => {
            this.setState({
              loading: false,
            })
          }
        )
    }
    this.setState({
      showEditConfirm: !this.state.showEditConfirm,
    })
  }

  private handleUserToEdit(userId: string, enabled: boolean, userName: string) {
    if (
      this.props.currentUser?.isAdmin ||
      (this.props.currentUser?.isInternalUser && this.props.currentUser?.isManager)
    ) {
      this.setState({
        userEnabled: enabled,
        selectedUserName: userName,
        userId,
        showEditConfirm: !this.state.showEditConfirm,
      })
    } else {
      return
    }
  }

  private onCompanyChanged(companyId: string) {
    this.setState({
      customerSearchString: '',
      selectedCompanyId: companyId,
      showCompanyPopup: !this.state.showCompanyPopup,
    })
  }

  private onTenantChanged(group: ITenantViewModel) {
    const selectedTenantId = group.id

    if (!selectedTenantId) {
      return
    }

    FrontendSettings.set(FrontendSettingsKeys.preselectedTenantId, selectedTenantId)

    this.setState({
      groupSearchString: '',
      loading: true,
      showGroupPopup: !this.state.showGroupPopup,
    })

    this.props.loadCustomersAdmin(selectedTenantId).then((customers) => {
      this.setState({ customers })
      this.props
        .loadUsers(selectedTenantId)
        .then((users) => this.setState({ users, loading: false, selectedCompanyId: '', selectedTenantId }))
    })
  }
}

/**
 * decide what part of state should be exposed
 * @param state  redux state
 * @param ownProps props of component
 */
function mapStateToProps({ appData }: State) {
  return {
    currentUser: appData.currentUser,
  }
}

/**
 * What action should be passed to component
 */

const mapDispatchToProps = {
  patchUserEnabled: wrapQuery((arg: { tenantId: string; userId: string; enabled: boolean }) =>
    customersApi.endpoints.patchUserEnabled.initiate(arg)
  ),
  loadUsers: wrapQuery((tenantId: string) => customersApi.endpoints.getTenantUsers.initiate(tenantId)),
  loadTenants: wrapQuery(() => customersApi.endpoints.getTenants.initiate()),
  loadCustomersAdmin: wrapQuery((tenantId: string) => customersApi.endpoints.getCustomers.initiate(tenantId)),
}

const connector = connect(mapStateToProps, mapDispatchToProps)
// eslint-disable-next-line react-refresh/only-export-components
export default connector(withTranslation('common')(UsersAdminPanel))
